/**
 * Auth Routing Utility
 * 
 * Centralized logic for routing users to correct dashboards based on their role.
 * Eliminates duplication and ensures consistent routing across the app.
 */

export type UserType = 'platform_admin' | 'org_admin' | 'employee' | 'individual';

/**
 * Get the appropriate dashboard route for a user type
 * @param userType - The user's role
 * @returns The dashboard path for that user type
 */
export function getDashboardRoute(userType?: string | null): string {
  switch (userType) {
    case 'platform_admin':
      return '/platform/dashboard';
    case 'org_admin':
      return '/admin/dashboard';
    case 'employee':
      return '/employee/dashboard';
    case 'individual':
      return '/individual/dashboard';
    default:
      return '/login';
  }
}

/**
 * Get the welcome/onboarding route for a user type
 * @param userType - The user's role
 * @returns The welcome path for that user type
 */
export function getWelcomeRoute(userType?: string | null): string {
  switch (userType) {
    case 'platform_admin':
      return '/platform/dashboard'; // Platform admins skip onboarding, go straight to dashboard
    case 'org_admin':
      return '/admin/welcome';
    case 'employee':
      return '/employee/welcome';
    case 'individual':
      return '/individual/welcome';
    default:
      return '/login';
  }
}

/**
 * Determine where to route a user after login based on their role and onboarding status
 * @param userType - The user's role
 * @param onboardingComplete - Whether the user has completed onboarding
 * @returns The appropriate route
 */
export function getRouteAfterLogin(
  userType?: string | null,
  onboardingComplete?: boolean
): string {
  // If onboarding not complete, send to welcome page
  if (!onboardingComplete) {
    return getWelcomeRoute(userType);
  }

  // Otherwise send to dashboard
  return getDashboardRoute(userType);
}

/**
 * Check if a user type is allowed to access a protected route
 * @param userType - The user's role
 * @param allowedRoles - The roles allowed for this route
 * @returns true if the user is allowed, false otherwise
 */
export function isUserAllowed(
  userType: string | undefined | null,
  allowedRoles: string[]
): boolean {
  if (!userType) return false;
  return allowedRoles.includes(userType);
}

/**
 * Get the correct dashboard section prefix for a user type
 * @param userType - The user's role
 * @returns The path prefix (e.g., '/admin', '/employee', '/individual')
 */
export function getDashboardSection(userType?: string | null): string {
  switch (userType) {
    case 'platform_admin':
      return '/platform';
    case 'org_admin':
      return '/admin';
    case 'employee':
      return '/employee';
    case 'individual':
      return '/individual';
    default:
      return '';
  }
}
